import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import PluginService from 'services/PluginService';
import { PLUGINS } from 'queries/query-keys';
import { getChosenBusiness } from 'store/authentication/selectors';
import { PluginContext } from 'ts-components/Plugins/context';
import { useKiCode } from 'ts-components/KiCode/useKiCode';
import { businessAppOpenArgs } from 'ts-components/KiCode/profiles';
import { useToast } from '__components/ToastProvider';
import { getOriginalError } from '__services/AxiosService';
import { getErrorMessage } from '__hooks/useErrors';
import { BUSINESS_APP_ACTIONS } from './constants';
import type { BusinessAppAction } from './helpers';
import type { BusinessApp, BusinessAppVersion } from './types';

export type PendingBusinessAppAction = {
  action: BusinessAppAction;
  app: BusinessApp;
  version?: BusinessAppVersion;
};

const CONFIRMED_ACTIONS: BusinessAppAction[] = [
  BUSINESS_APP_ACTIONS.deletePreview,
  BUSINESS_APP_ACTIONS.deleteApp,
  BUSINESS_APP_ACTIONS.deleteVersion,
  BUSINESS_APP_ACTIONS.disable,
  BUSINESS_APP_ACTIONS.publishPreview,
];

type UseBusinessAppActionsOptions = {
  onCompleted?: (request: PendingBusinessAppAction) => void;
};

export const useBusinessAppActions = ({
  onCompleted,
}: UseBusinessAppActionsOptions = {}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showToast, toastVariant] = useToast();
  const { refreshPluginArtifacts } = useContext(PluginContext);
  const business = useSelector(getChosenBusiness);
  const { open: openKiCode } = useKiCode();
  const [pending, setPending] = useState<PendingBusinessAppAction | null>(null);

  const run = useCallback(
    async ({ action, app, version }: PendingBusinessAppAction) => {
      const previewOwnerId = version?.preview_owner?.id;
      const handlers: Record<BusinessAppAction, () => Promise<unknown> | void> =
        {
          view: () => undefined,
          edit_with_kicode: () => openKiCode(businessAppOpenArgs(app.api_name)),
          edit_configuration: () => undefined,
          use_version: () =>
            version?.status === 'preview'
              ? PluginService.installBusinessAppPreview(
                  app.api_name,
                  previewOwnerId
                )
              : PluginService.switchBusinessAppToLive(app.api_name),
          publish_preview: () =>
            PluginService.promoteBusinessAppPreview(
              business.id,
              app.api_name,
              previewOwnerId
            ),
          delete_preview: () =>
            PluginService.deleteBusinessAppPreview(
              business.id,
              app.api_name,
              previewOwnerId
            ),
          delete_app: () =>
            PluginService.deleteBusinessApp(business.id, app.api_name),
          delete_version: () =>
            PluginService.deleteBusinessAppVersion(
              business.id,
              app.api_name,
              version?.version
            ),
          disable: () =>
            PluginService.updateBusinessPluginApp(app.api_name, {
              disabled: true,
            }),
          enable: () =>
            PluginService.updateBusinessPluginApp(app.api_name, {
              disabled: false,
            }),
        };
      const successMessages: Partial<Record<BusinessAppAction, string>> = {
        use_version: t("You're now using this version of {{name}}.", {
          name: app.name,
        }),
        publish_preview: t('Preview published'),
        delete_preview: t('Preview deleted'),
        delete_app: t('App deleted'),
        delete_version: t('Version deleted'),
        disable: t('{{name}} disabled', { name: app.name }),
        enable: t('{{name}} enabled', { name: app.name }),
      };

      try {
        await handlers[action]();
      } catch (error) {
        showToast({
          message:
            getErrorMessage(getOriginalError(error)) ||
            t('Something went wrong. Please try again.'),
          variant: toastVariant.FAILURE,
        });
        return false;
      }
      // Navigate before refetching a deleted version
      onCompleted?.({ action, app, version });
      if (
        action !== BUSINESS_APP_ACTIONS.view &&
        action !== BUSINESS_APP_ACTIONS.editConfiguration &&
        action !== BUSINESS_APP_ACTIONS.editWithKiCode
      ) {
        await queryClient.invalidateQueries({
          queryKey: PLUGINS.BUSINESS_APPS,
        });
        refreshPluginArtifacts(app.api_name);
        showToast({
          message: successMessages[action] ?? '',
          variant: toastVariant.SUCCESS,
        });
      }
      return true;
    },
    [
      business,
      onCompleted,
      openKiCode,
      queryClient,
      refreshPluginArtifacts,
      showToast,
      t,
      toastVariant,
    ]
  );

  const selectAction = useCallback(
    (request: PendingBusinessAppAction) =>
      CONFIRMED_ACTIONS.includes(request.action)
        ? setPending(request)
        : run(request),
    [run]
  );

  const confirmPending = useCallback(async () => {
    if (pending && (await run(pending))) {
      setPending(null);
    }
  }, [pending, run]);

  const cancelPending = useCallback(() => setPending(null), []);

  return { pending, selectAction, confirmPending, cancelPending };
};
